import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { CheckCircleIcon, HandPalmIcon } from '@phosphor-icons/react';
import { ValidationViolationStatus } from '@erp/shared-types';
import type {
  ValidationEvaluatedViolationDTO,
  ValidationViolationDTO,
} from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  useSetViolationExemption,
  useValidationViolations,
} from '@/api/rpc/hooks/inventory-management/useValidationRules';
import { ValidationViolationCard } from './EvaluationResultView';
import { formatValidationTimestamp } from './validationRuleDisplay';

const PAGE_SIZE = 20;

/** The two states of a violation someone can still act on. */
type ViolationTab =
  ValidationViolationStatus.Open | ValidationViolationStatus.Exempted;

/**
 * A persisted violation, in the shape the shared violation card renders. The
 * disagreement itself is stored under `details`, so the same card draws a
 * preview's findings and a recorded violation rather than two near-copies.
 */
function asEvaluatedViolation(
  violation: ValidationViolationDTO,
): ValidationEvaluatedViolationDTO {
  return {
    groupKeyValues: violation.groupKeyValues,
    validatedField: violation.validatedField,
    violationKey: violation.violationKey,
    observed: violation.details.observed,
    rows: violation.details.rows,
    rowsTruncated: violation.details.rowsTruncated,
    outcome: violation.details.outcome,
  };
}

/**
 * What a violation's own record adds to the disagreement: how long it has been
 * open, whether its rows are a sample, and the review that already covers it.
 */
function ViolationProvenance({
  violation,
}: {
  violation: ValidationViolationDTO;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-2 text-xs text-muted-foreground">
      <span>
        <T context="when a validation violation was first recorded">
          First seen <Var>{formatValidationTimestamp(violation.createdAt)}</Var>
        </T>
      </span>
      <span>
        <T context="when a validation violation was last confirmed by a run">
          Last seen <Var>{formatValidationTimestamp(violation.updatedAt)}</Var>
        </T>
      </span>
      {violation.details.rowsTruncated && (
        <Badge variant="outline">
          <T context="badge on a violation whose listed rows are a sample of a larger set">
            Sampled rows only
          </T>
        </Badge>
      )}
      {violation.changeSetId !== null && (
        <Link
          to="/item-master/reviews/$changeSetId"
          params={{ changeSetId: violation.changeSetId }}
          className="text-primary underline-offset-2 hover:underline"
        >
          <T context="link from a violation to the review that already stages its correction">
            Covered by review
          </T>
        </Link>
      )}
    </div>
  );
}

/**
 * The confirmation an exemption goes through: what exempting means, and an
 * optional reason shown wherever the exemption is. Mounted only while open, so
 * a reopened dialog never carries the previous attempt's text.
 */
function ExemptDialog({
  violation,
  onOpenChange,
}: {
  violation: ValidationViolationDTO;
  onOpenChange: (open: boolean) => void;
}) {
  const gt = useGT();
  const [reason, setReason] = useState('');
  const { mutate, isPending } = useSetViolationExemption();

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="title of the dialog that exempts one validation violation">
              Exempt this violation
            </T>
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          <T context="explains what exempting a validation violation does">
            It moves out of the open list and future runs will not stage it into
            reviews — even if the disagreement changes or comes back — until the
            exemption is removed.
          </T>
        </p>
        <Textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          maxLength={2000}
          placeholder={gt('Why is this acceptable? (optional)', {
            context: 'placeholder of the optional exemption reason field',
          })}
        />
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <T context="button closing the exemption dialog without exempting">
              Cancel
            </T>
          </Button>
          <Button
            type="button"
            disabled={isPending}
            onClick={() =>
              mutate(
                {
                  violationId: violation.id,
                  exempted: true,
                  reason: reason.trim() || undefined,
                },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            <T context="button confirming the exemption of one validation violation">
              Exempt
            </T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Provenance plus what someone can do about the violation: exempt an open one
 * (with an optional reason), or read and remove an existing exemption.
 */
function ViolationFooter({ violation }: { violation: ValidationViolationDTO }) {
  const [exemptDialogOpen, setExemptDialogOpen] = useState(false);
  const { mutate, isPending } = useSetViolationExemption();
  const isExempted = violation.status === ValidationViolationStatus.Exempted;

  return (
    <>
      <ViolationProvenance violation={violation} />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {isExempted ? (
          <>
            <span className="text-xs text-muted-foreground">
              <T context="when a validation violation was exempted">
                Exempted{' '}
                <Var>
                  {formatValidationTimestamp(
                    violation.exemptedAt ?? violation.updatedAt,
                  )}
                </Var>
              </T>
            </span>
            {violation.exemptionReason !== null &&
              violation.exemptionReason !== '' && (
                // The reason as the person typed it, rendered verbatim.
                <span className="text-xs italic text-muted-foreground">
                  “{violation.exemptionReason}”
                </span>
              )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-xs"
              disabled={isPending}
              onClick={() =>
                mutate({ violationId: violation.id, exempted: false })
              }
            >
              <T context="button putting an exempted violation back in play">
                Remove exemption
              </T>
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setExemptDialogOpen(true)}
          >
            <HandPalmIcon className="h-3.5 w-3.5" />
            <T context="button opening the dialog that exempts one violation as acceptable">
              Exempt…
            </T>
          </Button>
        )}
      </div>
      {exemptDialogOpen && (
        <ExemptDialog
          violation={violation}
          onOpenChange={setExemptDialogOpen}
        />
      )}
    </>
  );
}

/**
 * The rule's violations someone can still act on, in two piles: the open ones
 * (what still has to be dealt with) and the exempted ones (what a person ruled
 * acceptable — kept visible, because an exemption is a standing decision, not
 * a deletion).
 */
export function ValidationOpenViolationsPanel({ ruleId }: { ruleId: string }) {
  const gt = useGT();
  const [tab, setTab] = useState<ViolationTab>(ValidationViolationStatus.Open);
  const [page, setPage] = useState(1);
  const { violations, total, isLoading, error } = useValidationViolations({
    ruleId,
    status: tab,
    page,
    pageSize: PAGE_SIZE,
  });

  const firstShown = violations.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastShown = (page - 1) * PAGE_SIZE + violations.length;
  const showTab = (next: ViolationTab) => {
    setTab(next);
    setPage(1);
  };

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-sm font-medium">
            {tab === ValidationViolationStatus.Open ? (
              <T context="heading of the open violations section on a validation rule's page">
                Open violations
              </T>
            ) : (
              <T context="heading of the exempted violations section on a validation rule's page">
                Exempted violations
              </T>
            )}
          </h2>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              size="sm"
              className="h-6 px-2 text-xs"
              variant={
                tab === ValidationViolationStatus.Open ? 'secondary' : 'ghost'
              }
              onClick={() => showTab(ValidationViolationStatus.Open)}
            >
              <T context="tab listing a rule's open violations">Open</T>
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-6 px-2 text-xs"
              variant={
                tab === ValidationViolationStatus.Exempted
                  ? 'secondary'
                  : 'ghost'
              }
              onClick={() => showTab(ValidationViolationStatus.Exempted)}
            >
              <T context="tab listing a rule's exempted violations">Exempted</T>
            </Button>
          </div>
        </div>
        {total > 0 && (
          <span className="text-xs text-muted-foreground">
            {gt('{first}–{last} of {total}', {
              context:
                'which page of a validation rule’s violations is showing',
              first: String(firstShown),
              last: String(lastShown),
              total: String(total),
            })}
          </span>
        )}
      </div>

      {isLoading && violations.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <T context="loading state of a validation rule's violations">
            Loading violations…
          </T>
        </p>
      ) : error ? (
        // Failure must not fall through to the empty state below — "nothing is
        // open" on a failed query reads as the rule being clean.
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <T context="error state when a validation rule's violations could not be loaded">
            The violations could not be loaded.
          </T>{' '}
          {error.message}
        </div>
      ) : violations.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-10 text-center text-muted-foreground">
          <CheckCircleIcon className="h-7 w-7 text-green-600" />
          <p className="text-sm">
            {tab === ValidationViolationStatus.Open ? (
              <T context="empty state when a validation rule has no open violations">
                Nothing is open. Every group this rule has seen agrees.
              </T>
            ) : (
              <T context="empty state when a validation rule has no exempted violations">
                Nothing is exempted for this rule.
              </T>
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {violations.map((violation) => (
            <ValidationViolationCard
              key={violation.id}
              violation={asEvaluatedViolation(violation)}
              footer={<ViolationFooter violation={violation} />}
            />
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page === 1 || isLoading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <T context="button for the previous page of violations">Previous</T>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={lastShown >= total || isLoading}
            onClick={() => setPage((current) => current + 1)}
          >
            <T context="button for the next page of violations">Next</T>
          </Button>
        </div>
      )}
    </section>
  );
}
